import type {Object3D} from 'three';
import type {FeatureLine} from '../../../mesh/feature/FeatureLine';
import type {SketchGroup} from '../../../mesh/sketch/SketchGroup';
import type {
	SketchEntityReferenceCheckpointRuntimeOptions,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceCheckpointRuntime';
import type {
	RebuildSketchEntityReferencesRuntime,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildExecutor';
import type {
	TopologyEdgeSourceDescriptor,
	TopologySourceOwnerDescriptor,
} from '../model/ExternalGeometrySourceDescriptor';
import {
	createCanonicalTopologyIdentitySubset,
} from '../../../utils/reference/createCanonicalTopologyIdentitySubset';
import type {
	QueryConvertEntitiesFaceBoundariesPayload,
	QueryConvertEntitiesFaceBoundariesResult,
} from '../../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';
import type {
	TopologyOwnerRuntimeHost,
} from '../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';
import {
	TopologyOwnerRuntimeHostFactory,
} from '../source/topology/TopologyOwnerRuntimeHostFactory';

/**
 * Convert Entities 历史 checkpoint 运行时。
 *
 * 说明：
 * 1. 正式草图读取和引用 tag 写入委托给当前文档运行时；
 * 2. topology-edge 来源只读取 Task 1 返回的 checkpoint DTO；
 * 3. owner 缺失时直接返回 null，禁止回退最终 Viewer Scene；
 * 4. 本类不参与通用 Feature binding，也不拥有 Worker、Puppy CAD 内核 Shape 或订阅。
 */
export class SketchEntityReferenceCheckpointRuntime
implements RebuildSketchEntityReferencesRuntime {
	/** Scene/checkpoint 共用的无 Mesh topology host 工厂。 */
	private readonly topologyHostFactory = new TopologyOwnerRuntimeHostFactory();
	/** checkpoint owner 的稳定副本。 */
	private readonly owners: WorkerTopologyCheckpointOwner[];

	/**
	 * @param options 正式草图运行时与 checkpoint topology owners。
	 */
	public constructor(
		private readonly options: SketchEntityReferenceCheckpointRuntimeOptions,
	) {
		this.owners = options.owners.map((owner) => structuredClone(owner));
	}

	/**
	 * @inheritdoc
	 */
	public getSketchGroup(sketchId: string): SketchGroup | null {
		return this.options.baseRuntime.getSketchGroup(sketchId);
	}

	/**
	 * @inheritdoc
	 */
	public getSketchObject(sketchId: string, entityId: string): Object3D | null {
		return this.options.baseRuntime.getSketchObject(sketchId, entityId);
	}

	/**
	 * 历史模型边禁止读取最终 Scene FeatureLine。
	 *
	 * topology-edge 只允许通过 `resolveTopologyOwnerHost` 消费 checkpoint DTO。
	 */
	public getFeatureLine(
		_owningObjectId: string,
		_featureEdgeIndex: number,
	): FeatureLine | null {
		return null;
	}

	/**
	 * 按 Convert Entities 来源 owner 严格解析 checkpoint host。
	 *
	 * @param descriptor 持久化来源描述。
	 * @returns 命中唯一实体或同源分裂成员集合时返回无 Mesh host，否则返回 null。
	 */
	public resolveTopologyOwnerHost(
		descriptor: TopologyEdgeSourceDescriptor,
	): TopologyOwnerRuntimeHost | null {
		const matches = this.resolveOwners(descriptor.owner);
		if (matches.length === 0) {
			return null;
		}

		const hosts = matches.map((owner) => {
			/**
			 * 单一 checkpoint owner 是旧结果成员在当前历史位置的权威延续，
			 * 因此保留调用方请求的成员作用域，避免把一对一重放误判为“原成员消失”。
			 * 一个旧成员分裂为多个当前 owner 时则必须使用各自当前成员 ID，
			 * 让物理身份在互相隔离的局部 topology 中完成消歧。
			 */
			const requestedMemberId = descriptor.owner.resultObjectId?.trim();
			const topologyMemberId = matches.length === 1
				&& requestedMemberId
				&& owner.requestedResultObjectId === requestedMemberId
				? requestedMemberId
				: owner.resolvedResultObjectId;
			return this.topologyHostFactory.create({
				topology: owner.topology,
				topologyMemberId,
			});
		});
		return hosts.length === 1
			? hosts[0]!
			: this.topologyHostFactory.createCollection(hosts);
	}

	/**
	 * @inheritdoc
	 */
	public async queryTopologyFaceBoundaries(
		payload: QueryConvertEntitiesFaceBoundariesPayload,
	): Promise<QueryConvertEntitiesFaceBoundariesResult> {
		const owner = this.resolveOwner(payload.owner);
		if (!owner) {
			throw new Error(`checkpoint 中无法唯一解析 topology owner: ${payload.owner.ownerId}`);
		}
		const requestedFaceIndices = new Set(payload.faceIndices);
		const boundaries = (owner.convertEntitiesFaceBoundaries ?? [])
			.filter(boundary => requestedFaceIndices.has(boundary.faceIndex))
			.map(boundary => structuredClone(boundary));
		if (boundaries.length !== requestedFaceIndices.size) {
			throw new Error(`checkpoint 缺少完整 Face/Wire 边界: ${payload.owner.ownerId}`);
		}
		return {
			resolvedResultObjectId: owner.resolvedResultObjectId,
			topology: createCanonicalTopologyIdentitySubset(
				owner.topology,
				boundaries.map(boundary => boundary.faceIndex),
				boundaries.flatMap(boundary => [
					...boundary.outerWire.edgeIndices,
					...boundary.innerWires.flatMap(wire => wire.edgeIndices),
				]),
			),
			boundaries,
		};
	}

	/**
	 * 按 topology host 的身份优先级唯一解析 checkpoint owner。
	 *
	 * @param descriptor 持久 owner 描述。
	 * @returns 唯一 owner；缺失或歧义时返回 null。
	 */
	private resolveOwner(
		descriptor: TopologySourceOwnerDescriptor,
	): WorkerTopologyCheckpointOwner | null {
		const matches = this.resolveOwners(descriptor);
		return matches.length === 1 ? matches[0] ?? null : null;
	}

	/**
	 * 按 topology host 的身份优先级解析 checkpoint owner 集合。
	 *
	 * Feature 结果发生合法分裂时，同一实体血缘可以返回多个 owner；显式携带实体范围
	 * 的查询则禁止退回未限定 owner，避免 Face/Wire 侧车读取到其它结果体。
	 *
	 * @param descriptor 持久 owner 描述。
	 * @returns 允许参与当前来源重定位的 owner 集合。
	 */
	private resolveOwners(
		descriptor: TopologySourceOwnerDescriptor,
	): WorkerTopologyCheckpointOwner[] {
		const ownerMatches = this.owners.filter((owner) => (
			owner.ownerKind === descriptor.ownerKind
			&& owner.ownerId === descriptor.ownerId
		));
		const exactMatches = ownerMatches.filter((owner) => (
			(!descriptor.bodyBindingId || owner.bodyBindingId === descriptor.bodyBindingId)
			&& (!descriptor.modelBodyId || owner.modelBodyId === descriptor.modelBodyId)
			&& (!descriptor.modelBodyRevisionId
				|| owner.modelBodyRevisionId === descriptor.modelBodyRevisionId)
			&& (!descriptor.resultObjectId
				|| owner.requestedResultObjectId === descriptor.resultObjectId
				|| owner.resolvedResultObjectId === descriptor.resultObjectId)
		));
		const hasScopedIdentity = Boolean(
			descriptor.bodyBindingId
			|| descriptor.modelBodyId
			|| descriptor.modelBodyRevisionId
			|| descriptor.resultObjectId,
		);
		if (exactMatches.length > 0) {
			return exactMatches;
		}
		return hasScopedIdentity || ownerMatches.length !== 1
			? []
			: ownerMatches;
	}

	/**
	 * @inheritdoc
	 */
	public writeReferenceTag(targetObject: Object3D, referenceId: string): void {
		this.options.baseRuntime.writeReferenceTag?.(targetObject, referenceId);
	}

	/**
	 * @inheritdoc
	 */
	public clearReferenceTag(targetObject: Object3D): void {
		this.options.baseRuntime.clearReferenceTag?.(targetObject);
	}
}
